import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChannelDetail,
  LegalAcceptanceHistory,
  LegalDocumentAudience,
  LegalDocumentType,
  LegalOverview,
  PublicLegalDocument,
  VersionDetail,
  VersionSummary,
  acceptLegalDocuments,
  discardLegalDraft,
  getFranchiseLegalAcceptances,
  getLegalChannel,
  getLegalHistory,
  getLegalOverview,
  getLegalVersion,
  getPublicLegalDocument,
  getUserLegalAcceptances,
  publishLegalDraft,
  saveLegalDraft,
} from '@/api/legalDocuments';

export const LEGAL_OVERVIEW_KEY = ['legal-documents', 'overview'];
export const legalChannelKey = (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
) => ['legal-documents', 'channel', documentType, audience];
export const legalHistoryKey = (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
) => ['legal-documents', 'history', documentType, audience];
export const legalVersionKey = (id: string) => [
  'legal-documents',
  'version',
  id,
];

export const useLegalOverview = () =>
  useQuery<LegalOverview>({
    queryKey: LEGAL_OVERVIEW_KEY,
    queryFn: getLegalOverview,
    staleTime: 30 * 1000,
  });

export const useLegalChannel = (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
  enabled = true,
) =>
  useQuery<ChannelDetail>({
    queryKey: legalChannelKey(documentType, audience),
    queryFn: () => getLegalChannel(documentType, audience),
    enabled,
    // The editor must always seed from authoritative server state — never a
    // cached copy that may still hold a since-discarded/published draft.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

export const useLegalHistory = (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
  enabled = true,
) =>
  useQuery<VersionSummary[]>({
    queryKey: legalHistoryKey(documentType, audience),
    queryFn: () => getLegalHistory(documentType, audience),
    enabled,
  });

export const useLegalVersion = (id: string | null) =>
  useQuery<VersionDetail>({
    queryKey: legalVersionKey(id ?? ''),
    queryFn: () => getLegalVersion(id as string),
    enabled: !!id,
  });

// ---- acceptance audit trail (TCX-669) -------------------------------------

// `?? null` (not '') so a missing id is an explicit, unambiguous key segment —
// distinct from any real id and clearer in the query cache. The `enabled` guard
// below means the query never actually runs until the id exists.
export const userLegalAcceptancesKey = (userId?: string) => [
  'legal-documents',
  'acceptances',
  'user',
  userId ?? null,
];
export const franchiseLegalAcceptancesKey = (franchiseId?: string) => [
  'legal-documents',
  'acceptances',
  'franchise',
  franchiseId ?? null,
];

export const useUserLegalAcceptances = (userId?: string, enabled = true) =>
  useQuery<LegalAcceptanceHistory>({
    queryKey: userLegalAcceptancesKey(userId),
    queryFn: () => getUserLegalAcceptances(userId as string),
    enabled: enabled && !!userId,
  });

export const useFranchiseLegalAcceptances = (
  franchiseId?: string,
  enabled = true,
) =>
  useQuery<LegalAcceptanceHistory>({
    queryKey: franchiseLegalAcceptancesKey(franchiseId),
    queryFn: () => getFranchiseLegalAcceptances(franchiseId as string),
    enabled: enabled && !!franchiseId,
  });

type ChannelArgs = {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
};

export const useSaveLegalDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentType,
      audience,
      contentHtml,
    }: ChannelArgs & { contentHtml: string }) =>
      saveLegalDraft(documentType, audience, contentHtml),
    onSuccess: (_data, { documentType, audience }) => {
      queryClient.invalidateQueries({ queryKey: LEGAL_OVERVIEW_KEY });
      queryClient.invalidateQueries({
        queryKey: legalChannelKey(documentType, audience),
      });
    },
  });
};

export const useDiscardLegalDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentType, audience }: ChannelArgs) =>
      discardLegalDraft(documentType, audience),
    onSuccess: (_data, { documentType, audience }) => {
      queryClient.invalidateQueries({ queryKey: LEGAL_OVERVIEW_KEY });
      queryClient.invalidateQueries({
        queryKey: legalChannelKey(documentType, audience),
      });
    },
  });
};

export const usePublishLegalDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentType, audience }: ChannelArgs) =>
      publishLegalDraft(documentType, audience),
    onSuccess: (_data, { documentType, audience }) => {
      // Publishing a T&C also re-publishes Privacy, so refresh everything.
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      queryClient.invalidateQueries({
        queryKey: legalChannelKey(documentType, audience),
      });
    },
  });
};

// ---- public + re-acceptance (TCX-668) -------------------------------------

export const publicLegalKey = (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
) => ['legal-documents', 'public', documentType, audience];

// Public endpoint (no auth) — safe to run unauthenticated: it returns 200 for
// anonymous users, so it never triggers the axios 401 → forced-logout path.
export const usePublicLegalDocument = (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
) =>
  useQuery<PublicLegalDocument>({
    queryKey: publicLegalKey(documentType, audience),
    queryFn: () => getPublicLegalDocument(documentType, audience),
    // Published content must be live immediately — always revalidate on mount
    // instead of serving a stale cached copy for minutes after a publish.
    staleTime: 0,
    refetchOnMount: 'always',
  });

export const useAcceptLegalDocuments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptLegalDocuments,
    onSuccess: () => {
      // Re-fetch the profile so legalReacceptance.required flips false and the
      // blocking modal unmounts.
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};
